import React, { useState, useMemo, useRef } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { geoAlbersUsa } from "d3-geo";
import "./MapEditor.css";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Default dimension for ComposableMap is 800x600
const MAP_WIDTH = 800;
const MAP_HEIGHT = 600;

// Map state names to postal codes
const STATE_NAME_TO_CODE = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC', 'Puerto Rico': 'PR'
};

const MapEditor = ({ mapConfig = [], cityConfig = [], onChange }) => {
  const [showStateModal, setShowStateModal] = useState(false);
  const [showCityModal, setShowCityModal] = useState(false);
  
  const [selectedState, setSelectedState] = useState(null);
  const [selectedStateName, setSelectedStateName] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState('state'); // 'state' or 'city'

  // --- Dragging Text Handling ---
  const [draggingCity, setDraggingCity] = useState(null);
  const mapWrapperRef = useRef(null);

  const handleTextMouseDown = (e, city) => {
      e.stopPropagation();
      e.preventDefault();
      setDraggingCity({
          cityId: city.id,
          startX: e.clientX,
          startY: e.clientY,
          initialOffsetX: city.textOffsetX || 0,
          initialOffsetY: city.textOffsetY || -15
      });
  };

  const handleWrapperMouseMove = (e) => {
      if (!draggingCity) return;
      
      const svgRect = mapWrapperRef.current.getBoundingClientRect();
      const scaleX = MAP_WIDTH / svgRect.width;
      const scaleY = MAP_HEIGHT / svgRect.height; 

      const deltaX = (e.clientX - draggingCity.startX) * scaleX;
      const deltaY = (e.clientY - draggingCity.startY) * scaleY;

      const newCities = cityConfig.map(c => {
          if (c.id === draggingCity.cityId) {
             return {
                 ...c,
                 textOffsetX: Math.round(draggingCity.initialOffsetX + deltaX),
                 textOffsetY: Math.round(draggingCity.initialOffsetY + deltaY)
             };
          }
          return c;
      });
      onChange({ mapConfig, cityConfig: newCities });
  };

  const handleWrapperMouseUp = () => {
      setDraggingCity(null);
  };

  // Projection setup for inverting coordinates
  const projection = useMemo(() => {
    return geoAlbersUsa()
      .scale(1000)
      .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]);
  }, []);

  // --- State/Geography Handling ---
  const handleStateClick = (geo, event) => {
    if (editMode !== 'state') return; // Pass through if not in state mode
    
    event.stopPropagation();
    const stateName = geo.properties.name;
    const stateCode = STATE_NAME_TO_CODE[stateName] || stateName;
    const existing = mapConfig.find((c) => c.stateCode === stateCode) || {};
    
    setSelectedState(stateCode);
    setSelectedStateName(stateName);
    setFormData({
      stateCode: stateCode,
      color: existing.color || "#000000",
      hoverColor: existing.hoverColor || "#ff0000",
      url: existing.url || "",
    });
    setShowStateModal(true);
  };

  const saveStateConfig = () => {
    const newConfig = mapConfig.filter((c) => c.stateCode !== selectedState);
    if (formData.url || formData.color) {
        newConfig.push(formData);
    }
    onChange({ mapConfig: newConfig, cityConfig });
    setShowStateModal(false);
  };

  // --- City/Marker Handling ---
  const handleMapClick = (event) => {
    if (editMode !== 'city') return;

    const svgRect = event.currentTarget.getBoundingClientRect();
    const scaleX = MAP_WIDTH / svgRect.width;
    const scaleY = MAP_HEIGHT / svgRect.height;

    const x = (event.clientX - svgRect.left) * scaleX;
    const y = (event.clientY - svgRect.top) * scaleY;

    const coords = projection.invert([x, y]);
    
    if (coords) {
        setFormData({ name: "", latitude: coords[1], longitude: coords[0], color: "#FFD700", url: "" });
        setSelectedCity(null);
        setShowCityModal(true);
    }
  };

  const handleCityClick = (e, city) => {
      e.stopPropagation();
      // Always allow editing existing cities regardless of mode? 
      // Or maybe strictly follow mode. Let's allow editing existing cities always for convenience.
      setSelectedCity(city);
      setFormData({
          ...city
      });
      setShowCityModal(true);
  };

  const saveCityConfig = () => {
      let newCities = cityConfig;
      if (selectedCity && selectedCity.id) {
          // Update existing
          newCities = cityConfig.map(c => c.id === selectedCity.id ? {...c, ...formData} : c);
      } else {
          // Add new
          newCities = [...cityConfig, { ...formData, id: Date.now() }];
      }
      onChange({ mapConfig, cityConfig: newCities });
      setShowCityModal(false);
  };
  
  const removeCity = () => {
       const newCities = cityConfig.filter(c => c.id !== selectedCity.id);
       onChange({ mapConfig, cityConfig: newCities });
       setShowCityModal(false);
  }

  return (
    <div className="map-editor-container">
      <div className="map-controls d-flex justify-content-between align-items-center mb-3">
         <div>
             <span className="mr-3 font-weight-bold">Edit Mode:</span>
             <Button 
                variant={editMode === 'state' ? "primary" : "outline-primary"} 
                size="sm" 
                className="mr-2"
                onClick={() => setEditMode('state')}
             >
                 Select State
             </Button>
             <Button 
                variant={editMode === 'city' ? "primary" : "outline-primary"} 
                size="sm" 
                onClick={() => setEditMode('city')}
             >
                 Add City
             </Button>
         </div>
         <small className="text-muted">
             {editMode === 'state' ? "Click a state to configure it." : `Click anywhere to add a city. Text is draggable.`}
         </small>
      </div>

      {/* Wrapper to capture clicks on the map area */}
      <div 
        style={{ 
            width: "100%", 
            maxWidth: "800px", 
            margin: "0 auto", 
            cursor: draggingCity ? "move" : (editMode === 'city' ? "crosshair" : "default") 
        }} 
        onClick={handleMapClick}
        ref={mapWrapperRef}
        onMouseMove={handleWrapperMouseMove}
        onMouseUp={handleWrapperMouseUp}
        onMouseLeave={handleWrapperMouseUp}
      >
          <ComposableMap 
            projection="geoAlbersUsa" 
            width={MAP_WIDTH} 
            height={MAP_HEIGHT}
            style={{ width: "100%", height: "auto" }}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;
                  const stateCode = STATE_NAME_TO_CODE[stateName] || stateName;
                  const cur = mapConfig.find((s) => s.stateCode === stateCode);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={cur ? cur.color : "#DDD"}
                      stroke="#FFF"
                      onClick={(e) => {
                          handleStateClick(geo, e);
                      }}
                      style={{
                        default: { outline: "none", pointerEvents: draggingCity ? "none" : "all" },
                        hover: { fill: cur ? cur.hoverColor : "#F53", outline: "none", pointerEvents: draggingCity ? "none" : "all" },
                        pressed: { outline: "none", pointerEvents: draggingCity ? "none" : "all" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
            {cityConfig.filter(city => {
                const lat = parseFloat(city.latitude);
                const lon = parseFloat(city.longitude);
                if (isNaN(lat) || isNaN(lon)) return false;
                // Verify projection validity to prevent "destructure non-iterable" error
                // geoAlbersUsa returns null for coordinates outside the US.
                // We use the local projection instance to test.
                return projection([lon, lat]) !== null;
            }).map((city, index) => (
              <Marker 
                  key={index} 
                  coordinates={[city.longitude, city.latitude]} 
                  onClick={(e) => handleCityClick(e, city)} 
                  style={{ cursor: 'pointer'}}
              >
                {/* Star Icon */}
                <path
                    d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                    fill={city.color || "#FFD700"}
                    transform="translate(-12, -12) scale(0.8)" 
                    stroke="#FFF"
                    strokeWidth={1}
                />
                 <text 
                    textAnchor="middle" 
                    y={city.textOffsetY || -15}
                    x={city.textOffsetX || 0}
                    style={{ 
                        fontFamily: "system-ui", 
                        fill: city.color || "#FFD700", 
                        fontSize: "14px", 
                        fontWeight: "bold", 
                        cursor: "move", 
                        userSelect: "none" 
                    }}
                    onMouseDown={(e) => handleTextMouseDown(e, city)}
                    onClick={(e) => e.stopPropagation()} // Prevent map click/city click when clicking text
                 >
                    {city.name}
                 </text>
              </Marker>
            ))}
          </ComposableMap>
      </div>

      {/* State Modal */}
      <Modal show={showStateModal} onHide={() => setShowStateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit State: {selectedStateName || selectedState}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Color</Form.Label>
              <Form.Control type="color" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Hover Color</Form.Label>
              <Form.Control type="color" value={formData.hoverColor} onChange={(e) => setFormData({...formData, hoverColor: e.target.value})} />
            </Form.Group>
            <Form.Group>
              <Form.Label>URL</Form.Label>
              <Form.Control type="text" placeholder="https://..." value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStateModal(false)}>Close</Button>
          <Button variant="primary" onClick={saveStateConfig}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* City Modal */}
      <Modal show={showCityModal} onHide={() => setShowCityModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedCity ? 'Edit City' : 'Add City'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </Form.Group>
            <Row>
                <Col>
                    <Form.Group>
                      <Form.Label>Latitude</Form.Label>
                      <Form.Control type="number" step="0.0001" value={formData.latitude} onChange={(e) => setFormData({...formData, latitude: parseFloat(e.target.value)})} />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group>
                      <Form.Label>Longitude</Form.Label>
                      <Form.Control type="number" step="0.0001" value={formData.longitude} onChange={(e) => setFormData({...formData, longitude: parseFloat(e.target.value)})} />
                    </Form.Group>
                </Col>
            </Row>
            <Form.Group>
              <Form.Label>Color</Form.Label>
              <Form.Control type="color" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} />
            </Form.Group>
            <Form.Group>
              <Form.Label>URL</Form.Label>
              <Form.Control type="text" placeholder="https://..." value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {selectedCity && <Button variant="danger" onClick={removeCity}>Remove</Button>}
          <Button variant="secondary" onClick={() => setShowCityModal(false)}>Close</Button>
          <Button variant="primary" onClick={saveCityConfig}>Save Changes</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MapEditor;
